import React from 'react';

import UserName from '~/components/Client/UserName';
import { DateText } from '~/eds/src/components/DateText/DateText';
import { FormattedMessage } from 'react-intl';
import Box from '~/ui/components/Box/Box';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Icon from '~/ui/components/Icon/Icon';
import Text from '~/ui/components/Text/Text';
import { getFileIcon } from '~/utils/files';

export function getLatestVersion(documentVersions) {
  return documentVersions?.length ? documentVersions[0].versionNumber : 1;
}

export function getVersionLabelText(versionNumber, intl) {
  if (!versionNumber) return null;
  return intl.formatMessage(
    {
      id: 'DocVersionMenu.versionLabel',
      defaultMessage: 'Version {versionNumber}',
      description:
        'Label for document version number. Example: versionNumber = "1.2"',
    },
    { versionNumber },
  );
}

function renderItemDetail(versionsObject, item) {
  const {
    isGenerated,
    referenceVersionId,
    targetVersionId,
    userId,
    versionNumber,
  } = item;
  const isCounterpartyEditVersion = isGenerated && !targetVersionId;
  if (versionNumber === 1 || isCounterpartyEditVersion) {
    return (
      <>
        <FormattedMessage
          id="DocVersionMenu.createdBy"
          defaultMessage="Created by {userName}"
          description="Label for document created by user. Example: userName = 'John Smith'"
          values={{
            userName: <UserName userId={userId} />,
          }}
        />
      </>
    );
  } else if (isGenerated) {
    if (referenceVersionId === targetVersionId) {
      return (
        <>
          <FormattedMessage
            id="DocVersionMenu.savedBy"
            defaultMessage="Saved by {userName}"
            description="Label for document saved by user. Example: userName = 'John Smith'"
            values={{
              userName: <UserName userId={userId} />,
            }}
          />
        </>
      );
    } else {
      return (
        <>
          <FormattedMessage
            id="DocVersionMenu.redlineComparison"
            defaultMessage="Redline of Version {targetVersion} compared to Version {referenceVersion} saved by {UserName}"
            description="Label for redline comparison between versions. Example: targetVersion = '2.1', referenceVersion = '2.0', UserName = 'John Smith'"
            values={{
              targetVersion: versionsObject[targetVersionId],
              referenceVersion: versionsObject[referenceVersionId],
              UserName: <UserName userId={userId} />,
            }}
          />
        </>
      );
    }
  } else {
    return (
      <>
        <FormattedMessage
          id="DocVersionMenu.uploadedBy"
          defaultMessage="Uploaded by {UserName}"
          description="Label for document uploaded by user. Example: UserName = 'John Smith'"
          values={{
            UserName: <UserName userId={userId} />,
          }}
        />
      </>
    );
  }
}

export function getVersionNumberIds(versionsArray) {
  return Object.fromEntries(
    Object.entries(versionsArray).map(([_key, val]) => [
      [val.id],
      val.versionNumber,
    ]),
  );
}

export function getOptionLabel({
  versionsObject,
  expanded,
  version,
  isSelected,
  disabled,
  intl,
}) {
  const { fileType, isCounterparty, lastUpdated, versionNumber } = version;

  const dummyFile = new File([], `file.${fileType}`); // create a dummy file for reference with file APIs.
  return (
    <Box
      p={4}
      disabled={disabled}
      sx={{
        width: '100%',
        backgroundColor: isSelected ? 'gray-200' : 'transparent',
      }}
    >
      <FlexLayout alignItems="center" justifyContent="space-between" space={1}>
        <FlexLayout space={1}>
          <Icon icon={getFileIcon(dummyFile)} size="s" />
          <Text color="gray-700" variant="body1">
            {getVersionLabelText(versionNumber, intl)}
          </Text>
        </FlexLayout>
        {isCounterparty && (
          <FlexLayout
            p={2}
            sx={{
              border: 'border',
              borderRadius: '4px',
              backgroundColor: 'white',
            }}
          >
            <Text color="black-alpha-75" variant="tiny">
              <FormattedMessage
                id="DocVersionMenu.counterpartyLabel"
                defaultMessage="Counterparty"
                description="Label for counterparty version"
              />
            </Text>
          </FlexLayout>
        )}
      </FlexLayout>
      {expanded && (
        <>
          <Box>
            <Text color="gray-900" variant="tiny">
              {renderItemDetail(versionsObject, version)}
            </Text>
          </Box>
          <Box>
            <DateText
              date={lastUpdated ? new Date(lastUpdated) : undefined}
              format="duration"
              enableTick
              variant="tiny"
            />
          </Box>
        </>
      )}
    </Box>
  );
}
